import {BitmapText, Container, Graphics} from "pixi.js";
import {DrawFrmThing} from "../../drawfrm/DrawFrmThing";
import {FrmVar} from "../../drawfrm/FrmVar";
import {MdFrameItem} from "../../model/frame/MdFrameBlock";
import {inches} from "../../const";
import {FrameGridView} from "./FrameGridScene";
import {FrameBlockColumn} from "./FrameBlockColumn";
import {FrameBlockRow} from "./FrameBlockRow";
import {FrameBlockInnerDimension} from "./FrameBlockInnerDimension";
import {DimensionLines} from "../../shared-2d/GlassLabel";
import {LiteFace} from "./item/LiteFace";
import {DoorFace} from "./item/DoorFace";
import {FixedFace} from "./item/FixedFace";
import {VoidFace} from "./item/VoidFace";
import {ThresholdFace} from "./item/ThresholdFace";
import {DoorSlotFace} from "./item/DoorSlotFace";
import {isSecondaryDoors} from "../../model/frame/MdFrameSpec";
import {EdgeCover, EdgeLeaf, EdgeSide, EdgeSlope, FrameBlockExt, itemDimensionAxes, LayoutRect} from "./FrameBlockExt";
import {ElevationApp} from "../../ElevationApp";
import {slopesOver, applyShortEndEdit, beginTallEndEdit, finishTallEndEdit, resizeSplitCell, ShortEndEdit, TallEndEdit} from "./BlockOps";
import {LinearDimension} from "./LinearDimension";

// where the inside dimension lines sit in from the cell's edges (see
// FrameBlockInnerDimension).
const DIM_INSET = 4 * inches;

export class FrameBlockItem extends DrawFrmThing {

    view: FrameGridView;
    parent: FrameBlockRow|FrameBlockColumn;
    model: MdFrameItem;

    // a leaf cell has no edge sticks of its own — these satisfy the TFrameBlock shape
    // the layout iterates over.
    layoutRect?: LayoutRect;
    renderOutline() {}
    outlineMats() { return []; }
    layoutVoidParts() {}

    // a leaf is one cell along the edge (see TFrameBlock). A void has no stick, nor
    // does the threshold on the bottom: it is the door's sill itself. A stick over an
    // open leaf is a model fault (see BlockOps.normalizeEdgeSticks), except in the
    // middle of a drag, which the release puts right.
    collectEdgeLeaves(side: EdgeSide, cover: EdgeCover, out: EdgeLeaf[]) {
        const open = this.model.kind === "void" || (this.model.kind === "threshold" && side === "bottom");
        if(open && cover && !this.elev.dragging) {
            console.warn(`frame grid: stick ${cover.stick.id} spans ${this.model.kind} ${this.model.id} on the ${side}`);
        }
        out.push({cell: this, cover: open ? null : cover});
    }

    kiwiWidth: any;
    width: FrmVar;

    kiwiHeight: any;
    height: FrmVar;

    inner: BlockItemDrawable;
    // the model kind `inner` was built for, so reconcileInner() knows when a
    // convert (lite <-> fixedPanel) has made it stale.
    private innerKind!: string;

    private label: BitmapText | null = null;
    private innerDimension: FrameBlockInnerDimension;
    private elev: ElevationApp;

    // how far the drawn face runs past the cell's bottom: the door leaf draws down over
    // the threshold cell under it (see setDrawnBottomExtension). Render-only: the cell's
    // layoutRect/size stay as laid out.
    private drawnBottomExtension = 0;

    // the sloped sticks cutting into this cell, by side (see TFrameBlock.setEdgeSlope).
    // `nextSlopes` collects a render's slopes until endEdgeSlopes applies them.
    private slopes: Partial<Record<EdgeSide, EdgeSlope>> = {};
    private nextSlopes: Partial<Record<EdgeSide, EdgeSlope>> = {};

    // the dimension at a sloped cell's short end (the tall end has the usual inner
    // dimension). Built the first time a slope needs it.
    private shortDim: LinearDimension | null = null;

    // a number edit under a sloped stick, finished by relayout once the layout has run.
    private tallEndEdit: TallEndEdit | null = null;
    private shortEndEdit: ShortEndEdit | null = null;

    constructor(item: FrameGridView, opts: MdFrameItem, parent: FrameBlockRow|FrameBlockColumn) {
        super();

        this.view = item;
        this.elev = item.elev;
        this.parent = parent;
        this.model = opts;

        if(opts.kind === "lite" || opts.kind === "fixedPanel" || opts.kind === "void") {

            this.width = new FrmVar(opts.id + ":width:calc", 12 * inches);
            this.height = new FrmVar(opts.id + ":height:calc", 12 * inches);

            // the flow-axis dimension (width in a row, height in a column) is the
            // cell's editable size. Back it with the model's `size` FrmVar —
            // materializing one here if the model doesn't carry it yet — so a user
            // edit (which pins it via userSet/isModified) lives on the model and
            // survives the grid rebuild a structural op (split/insert) triggers.
            // Without this an un-sized cell edited a throwaway `_*_calc` var that
            // was discarded on rebuild, losing the user's dimension.
            if(!opts.size) opts.size = new FrmVar(opts.id + ":size", 12 * inches);

            if(parent instanceof FrameBlockRow) {
                this.width = opts.size;
            } else if(parent instanceof FrameBlockColumn) {
                this.height = opts.size;
            }
        } else if(opts.kind === "threshold") {
            // the threshold spans its column; its height is thresholdHeight (dictated).
            this.width = new FrmVar(opts.id + ":width:calc", 12 * inches);
            this.height = opts.size;
        } else {
            this.width = opts.width;
            this.height = opts.height;
        }

        const axes = itemDimensionAxes(opts, parent.model);
        this.innerDimension = new FrameBlockInnerDimension(this, {axes});

        this.inner = this.buildInner();

        // the "Show All Dimensions" view toggle drives whether the door face's
        // pair-gap and threshold dimensions are drawn — and, in the screen
        // workspace, the door's own width/height; redraw when it flips.
        this.trackSubscription(this.elev.viewSettings.dimShowAll.subscribe(() => this.draw()));

        // the door face draws the leaf's glass lites, so a lite add/remove needs a
        // redraw to stay in sync with the door-elevation view.
        this.trackSubscription(this.elev.doorSpec.litesChanged.subscribe(() => this.draw()));

        this.draw();
    }

    // build the drawable for the current model kind, recording which kind it was
    // built for so reconcileInner() can detect a convert.
    private buildInner(): BlockItemDrawable {
        this.innerKind = this.faceKind();
        // track() returns undefined once destroyed, so build the face first and
        // return it directly — buildInner always yields a drawable.
        const face = this.model.kind === "lite" ? new LiteFace()
            : this.model.kind === "fixedPanel" ? new FixedFace()
            : this.model.kind === "void" ? new VoidFace()
            : this.model.kind === "threshold" ? new ThresholdFace()
            : isSecondaryDoors(this.elev.frameSpec, this.model) ? new DoorSlotFace()
            : new DoorFace();
        this.track(face);
        return face;
    }

    // the face this item draws with: its kind, or "doorSlot" for a secondary door slot.
    private faceKind(): string {
        return isSecondaryDoors(this.elev.frameSpec, this.model) ? "doorSlot" : this.model.kind;
    }

    // swap `inner` when the model kind has changed (lite <-> fixedPanel). Sizing
    // is identical between those kinds, so only the face needs rebuilding.
    reconcileInner() {
        if(this.faceKind() === this.innerKind) return;
        this.inner.destroy();
        this.inner = this.buildInner();
        this.draw();
    }

    getItems() {
        return [this];
    }

    // see TFrameBlock.
    beginEdgeSlopes() {
        this.nextSlopes = {};
    }

    setEdgeSlope(side: EdgeSide, slope: EdgeSlope) {
        this.nextSlopes[side] = slope;
    }

    endEdgeSlopes() {
        const same = (["top", "bottom", "left", "right"] as EdgeSide[]).every(side => {
            const a = this.slopes[side];
            const b = this.nextSlopes[side];
            return a?.start === b?.start && a?.end === b?.end;
        });
        this.slopes = this.nextSlopes;
        if(!same) this.draw();
    }

    // the drawn face when a sloped stick cuts into this cell: the rect less what the
    // slope takes off each side. Undefined for a plain rect.
    private facePolygon(): {x: number; y: number}[] | undefined {
        const s = this.slopes;
        if(!s.top && !s.bottom && !s.left && !s.right) return undefined;
        const w = this.width.get();
        const h = this.height.get() + this.drawnBottomExtension;
        const top = s.top ?? {start: 0, end: 0};
        const bottom = s.bottom ?? {start: 0, end: 0};
        const left = s.left ?? {start: 0, end: 0};
        const right = s.right ?? {start: 0, end: 0};
        return [
            {x: left.start, y: top.start},
            {x: w - right.start, y: top.end},
            {x: w - right.end, y: h - bottom.end},
            {x: left.end, y: h - bottom.start},
        ];
    }

    // the cell's cut-out for the glass schedule: its laid-out rect, with the short
    // height too when a sloped head or sill cuts it (the rect height is the tall end).
    cutOut(): {width: number; height: number; heightShort?: number} {
        const w = this.width.get();
        const h = this.height.get();
        const ends = this.slopedEnds("height");
        const short = ends ? Math.min(ends.startEnd, ends.endEnd) : h;
        return {width: w, height: h, heightShort: short < h - 1e-6 ? short : undefined};
    }

    // the span of a sloped cell's short height in its own coords (from the deepest
    // point of the slope to the far edge), or null when no head or sill slopes.
    shortHeightSpan(): {y0: number; y1: number} | null {
        const s = this.slopes;
        const h = this.height.get();
        const top = s.top ? Math.max(s.top.start, s.top.end) : 0;
        const bottom = s.bottom ? Math.max(s.bottom.start, s.bottom.end) : 0;
        if(top === 0 && bottom === 0) return null;
        return {y0: top, y1: h - bottom};
    }

    // a sloped cell's two heights (left and right end) or widths (top and bottom end),
    // or null when no stick slopes along that axis.
    private slopedEnds(axis: "width" | "height"): {startEnd: number; endEnd: number} | null {
        const s = this.slopes;
        if(axis === "height") {
            if(!s.top && !s.bottom) return null;
            const h = this.height.get();
            return {startEnd: h - (s.top?.start ?? 0) - (s.bottom?.start ?? 0), endEnd: h - (s.top?.end ?? 0) - (s.bottom?.end ?? 0)};
        }
        if(!s.left && !s.right) return null;
        const w = this.width.get();
        return {startEnd: w - (s.left?.start ?? 0) - (s.right?.start ?? 0), endEnd: w - (s.left?.end ?? 0) - (s.right?.end ?? 0)};
    }

    // see TFrameBlock. The stick's tall end is where it cuts nothing off the cell; a cell
    // cut on both of its sides across the axis, or only mid-slope, has none.
    stickTallEndCross(axis: "width" | "height"): number | null {
        const ends = this.slopedEnds(axis);
        if(!ends) return null;
        const startTall = ends.startEnd >= ends.endEnd;
        const s = this.slopes;
        const sides: EdgeSide[] = axis === "height" ? ["top", "bottom"] : ["left", "right"];
        const depth = Math.max(...sides.map(side => (startTall ? s[side]?.start : s[side]?.end) ?? 0));
        if(depth > 1e-6) return null;
        const across = axis === "height" ? this.width.get() : this.height.get();
        return startTall ? DIM_INSET : across - DIM_INSET;
    }

    // place the inner dimension of a sloped axis at the cell's tall end, and draw the
    // short end's dimension beside the other edge. Each edit moves only its own end:
    // the short end sets the sloped stick's drop (see beginTallEndEdit for the tall end).
    private drawSlopeDimensions() {
        const spec = this.elev.frameSpec;
        const ends = this.slopedEnds("height") ? {axis: "height" as const, ...this.slopedEnds("height")!} : this.slopedEnds("width") ? {axis: "width" as const, ...this.slopedEnds("width")!} : null;
        const w = this.width.get();
        const h = this.height.get();

        if(!ends) {
            this.innerDimension.setCross("width", null);
            this.innerDimension.setCross("height", null);
            this.shortDim?.hide();
            return;
        }

        // the tall end is where the sloped stick leaves the cell its full size.
        const startTall = ends.startEnd >= ends.endEnd;
        const across = ends.axis === "height" ? w : h;
        const tallCross = startTall ? DIM_INSET : across - DIM_INSET;
        const shortCross = startTall ? across - DIM_INSET : DIM_INSET;
        this.innerDimension.setCross(ends.axis, tallCross);
        this.innerDimension.setCross(ends.axis === "height" ? "width" : "height", null);

        const short = startTall ? ends.endEnd : ends.startEnd;
        const tall = startTall ? ends.startEnd : ends.endEnd;
        if(this.elev.viewSettings.externalDimensions.value || short === tall) {
            this.shortDim?.hide();
            return;
        }

        if(!this.shortDim) this.shortDim = new LinearDimension(this.graphic, this.elev.draw);
        const stick = slopesOver(spec, this.model.id).get(ends.axis === "height" ? (this.slopes.top ? "top" : "bottom") : (this.slopes.left ? "left" : "right"));
        // the short end runs from where the slope cuts in at that end to the far edge.
        const s = this.slopes;
        const cutAt = (side: EdgeSide) => (startTall ? s[side]?.end : s[side]?.start) ?? 0;
        const from = ends.axis === "height" ? cutAt("top") : cutAt("left");
        this.shortDim.draw({
            axis: ends.axis === "height" ? "vertical" : "horizontal",
            from,
            to: from + short,
            cross: shortCross,
            value: short,
            modified: !!stick?.slope?.drop.isModified(),
            onSet: (v) => {
                if(!stick) return;
                const edit: ShortEndEdit = {stickId: stick.id, cellId: this.model.id, axis: ends.axis, value: v};
                if(!applyShortEndEdit(spec, edit)) return;
                this.shortEndEdit = edit;
                this.relayout();
            },
            onClear: () => {},
        });
    }

    // InnerDimensionTarget: the dimension annotations attach to this item's
    // graphic (positioned at the item's top-left), so they pan with the item.
    get dimensionContainer() {
        return this.graphic;
    }

    // a lite alone in a split part of the frame edge resizes through a step, so the
    // parts beside it keep their size (see resizeSplitCell).
    onDimensionEdit(axis: "width" | "height", delta: number): boolean | void {
        if(this.model.kind !== "lite" && this.model.kind !== "fixedPanel") return;

        const spec = this.elev.frameSpec;

        const sizeVar = axis === "height" ? this.height : this.width;
        const tallEnd = beginTallEndEdit(spec, this.model.id, axis, sizeVar, delta);
        if(tallEnd === "refused") {
            this.elev.draw.showError("The short end of the slope must be at least 3\".");
            return false;
        }
        if(tallEnd) delta = tallEnd.delta;

        const result = resizeSplitCell(spec, this.model.id, delta, undefined, axis);
        if(result === "refused") {
            this.elev.draw.showError("Can't step the frame for this size. Each step must be at least 3\".");
            return false;
        }

        this.tallEndEdit = tallEnd?.edit ?? null;
        if(result === "rebuilt") {
            spec.assignLiteMarks();
            spec.frameBlocksChanged.emit(true);
        }
    }

    // re-flow the whole grid after a dimension edit, then nudge the scene layout
    // so it re-fits to the grid's new bounds.
    relayout() {
        this.view.render();
        const tallEnd = this.tallEndEdit;
        this.tallEndEdit = null;
        if(tallEnd && finishTallEndEdit(this.elev.frameSpec, tallEnd)) this.view.render();
        const shortEnd = this.shortEndEdit;
        this.shortEndEdit = null;
        for(let i = 0; shortEnd && i < 8 && applyShortEndEdit(this.elev.frameSpec, shortEnd); i++) this.view.render();
        this.changed.emit("re-layout");
    }

    reposition(x: number, y: number) {
        this.graphic.position.set(x, y);
        this.layoutRect = {x, y, width: this.width.get(), height: this.height.get()};
        this.label?.position.set(this.width.get() / 2, this.height.get() / 2);
        this.draw();
    }

    getWidth(strict: boolean) {
        return this.width.get()
    }

    getHeight(strict: boolean) {
        return this.height.get();
    }

    // draw the face `ext` past the cell's bottom (the door leaf over its threshold).
    // Redraws if it changed.
    setDrawnBottomExtension(ext: number) {
        if(this.drawnBottomExtension === ext) return;
        this.drawnBottomExtension = ext;
        this.draw();
    }

    draw() {
        this.drawSlopeDimensions();
        this.inner.draw({
            startX: 0,
            startY: 0,
            endX: this.width.get(),
            endY: this.height.get() + this.drawnBottomExtension,
            polygon: this.facePolygon(),

            elev: this.elev,
            graphics: this.lines,
            block: this.model,
            dimensionContainer: this.dimensionContainer,
            dimensionLines: this.dimensionLines(),
            flowAxis: this.parent instanceof FrameBlockColumn ? "vertical" : "horizontal",
            size: this.parent instanceof FrameBlockColumn ? this.height : this.width,
            relayout: () => this.relayout(),
        });

        // the door cell's width is the door's actual width; on the frame-only views
        // that's frame context rather than the subject, so it only shows under "show
        // all dimensions" (matches DoorFace's leaf height).
        const view = this.elev.viewSettings;
        const screenDoorHidden = this.model.kind === "doors"
            && view.frameOnly() && !view.dimShowAll.value;
        this.innerDimension.setVisible(!screenDoorHidden);
    }

    // the dimension lines over this cell, from its top-left: its own, and those of the
    // blocks around it (e.g. a row's height line runs across its first cell).
    private dimensionLines(): DimensionLines {
        const out: DimensionLines = {horizontal: [], vertical: []};
        const add = (insets: {top: number; left: number}, x: number, y: number) => {
            if(insets.top > 0) out.horizontal.push(y + insets.top);
            if(insets.left > 0) out.vertical.push(x + insets.left);
        };

        add(this.innerDimension.lineInsets(), 0, 0);
        const shortLine = this.shortDim?.lineAt();
        if(shortLine) {
            if(shortLine.axis === "horizontal") out.horizontal.push(shortLine.cross);
            else out.vertical.push(shortLine.cross);
        }

        const me = this.layoutRect;
        if(!me) return out;
        for(let b: FrameBlockExt | undefined = this.parent; b?.layoutRect; b = b.parent) {
            add(b.dimensionLineInsets(), b.layoutRect.x - me.x, b.layoutRect.y - me.y);
        }
        return out;
    }

    // the active door leaf's box in frame-grid (flat) coords when this cell is a
    // door, else null — for the hardware export to dimension door-mounted hardware
    // (hosted on the active leaf, positioned from its top-left). Valid after draw().
    activeDoorLeafRect(): {left: number; top: number; width: number; height: number} | null {
        if(this.model.kind !== "doors" || !this.layoutRect) return null;
        if(!(this.inner instanceof DoorFace)) return null;

        const r = this.inner.getActiveLeafRect();
        if(!r) return null;

        return {
            left: this.layoutRect.x + r.doorLeft,
            top: this.layoutRect.y + r.doorTop,
            width: r.doorRight - r.doorLeft,
            height: r.doorBottom - r.doorTop,
        };
    }
}

export type BlockItemDrawProps = {
    startX: number;
    startY: number;
    endX: number;
    endY: number;
    // the face's outline when a sloped stick cuts into the cell (local coords), else
    // undefined: the face is the start/end rect.
    polygon?: {x: number; y: number}[];

    elev: ElevationApp;
    block: MdFrameItem;
    graphics: Graphics;
    // the item's outer container (positioned at its top-left, scene coords); door
    // dimensions attach here so they survive the per-draw `graphics` clear and pan
    // with the item.
    dimensionContainer: Container;
    // the dimension lines drawn over this cell, its own and its blocks', in the cell's
    // coords (see FrameBlockItem.dimensionLines), so the face can keep its labels clear
    // of them.
    dimensionLines: DimensionLines;
    // the axis the cell's parent lays its cells along, and the cell's size on it.
    flowAxis: "horizontal" | "vertical";
    size: FrmVar;
    // re-run the grid layout after a dimension edit (mirrors the inner-dimension
    // edit flow).
    relayout: () => void;
}

type BlockItemDrawable = {
    draw(props: BlockItemDrawProps): void;
    destroy(): void;
}